// Aquafeed catalogue: instant filters and sorting over the server-rendered
// cards, add to cart from the card (opens the cart drawer, where the invoice
// fields and checkout are).
(() => {
  if (window.aqCatalogLoaded) return;
  window.aqCatalogLoaded = true;

  const fill = (template, values) =>
    Object.entries(values).reduce((text, [key, value]) => text.split(`[${key}]`).join(value), template);

  function formatMoney(cents, format) {
    const value = Number(cents) / 100;
    const group = (num, decimals, thousands, decimal) => {
      const [whole, frac] = num.toFixed(decimals).split('.');
      const withSep = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? withSep + decimal + frac : withSep;
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return group(value, 0, ',', '.');
        case 'amount_with_comma_separator':
          return group(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return group(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return group(value, 2, ' ', ',');
        default:
          return group(value, 2, ',', '.');
      }
    });
  }

  const formatKg = (kg) => (Number.isInteger(kg) ? String(kg) : kg.toFixed(1).replace('.', ','));

  // Tell the base theme the cart changed so its cart icon count updates.
  function notifyTheme(cart) {
    // Horizon: the cart icon listens for Shopify's standard lines-update event.
    const standard = new Event('shopify:cart:lines-update', { bubbles: true });
    standard.promise = Promise.resolve({ cart: { totalQuantity: cart.item_count }, detail: { itemCount: cart.item_count } });
    document.dispatchEvent(standard);
    // Dawn and others.
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { resource: cart, sourceId: 'aq-catalog', data: { source: 'aq-catalog', itemCount: cart.item_count } },
      })
    );
    document.querySelectorAll('.cart-count-bubble span[aria-hidden], [data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
    });
  }

  // ---- one catalogue section ----
  function initCatalog(root) {
    const config = JSON.parse(root.querySelector('[data-aq-config]').textContent);
    const grid = root.querySelector('[data-aq-grid]');
    if (!grid) return; // search with no product results
    const items = [...grid.querySelectorAll('[data-aq-item]')];
    const groups = [...root.querySelectorAll('[data-group]')];
    const inStock = root.querySelector('[data-aq-instock]');
    const sort = root.querySelector('[data-aq-sort]');
    const count = root.querySelector('[data-aq-count]');
    const empty = root.querySelector('[data-aq-empty]');
    const resets = root.querySelectorAll('[data-aq-reset]');

    // Pellet sizes in numeric order (0.5mm, 0.8mm, 1.1mm …).
    root.querySelectorAll('[data-aq-sort-chips]').forEach((wrap) => {
      [...wrap.children]
        .sort((a, b) => parseFloat(a.dataset.value.replace(',', '.')) - parseFloat(b.dataset.value.replace(',', '.')))
        .forEach((chip) => wrap.appendChild(chip));
    });

    function selected(group) {
      return [...group.querySelectorAll('[aria-pressed="true"]')].map((chip) => chip.dataset.value);
    }

    function apply() {
      let shown = 0;
      let active = inStock && inStock.checked;
      items.forEach((item) => {
        let visible = !(inStock && inStock.checked && item.dataset.available !== 'true');
        groups.forEach((group) => {
          const values = selected(group);
          if (!values.length) return;
          active = true;
          let have;
          if (group.dataset.group === 'type') have = [item.dataset.type];
          else if (group.dataset.group === 'size') have = item.dataset.sizes.split('|');
          else have = item.dataset.cols.split(' ');
          if (!values.some((value) => have.includes(value))) visible = false;
        });
        item.hidden = !visible;
        if (visible) shown += 1;
      });

      const order = sort ? sort.value : 'default';
      const sorted = [...items].sort((a, b) => {
        if (order === 'price-asc') return a.dataset.price - b.dataset.price;
        if (order === 'price-desc') return b.dataset.price - a.dataset.price;
        // Recommended: what can be ordered now first, then the collection order.
        const stock = (b.dataset.available === 'true') - (a.dataset.available === 'true');
        return stock || a.dataset.index - b.dataset.index;
      });
      sorted.forEach((item) => grid.appendChild(item));

      if (count) count.textContent = fill(config.strings.count, { n: shown });
      if (empty) empty.hidden = shown > 0;
      resets.forEach((button) => {
        if (!button.closest('[data-aq-empty]')) button.hidden = !(active || order !== 'default');
      });
      writeUrl();
    }

    // Filters live in the URL so a filtered list can be shared or bookmarked.
    function writeUrl() {
      const params = new URLSearchParams(window.location.search);
      groups.forEach((group) => {
        const values = selected(group);
        if (values.length) params.set(group.dataset.group, values.join(','));
        else params.delete(group.dataset.group);
      });
      if (inStock && inStock.checked) params.set('stoc', '1');
      else params.delete('stoc');
      if (sort && sort.value !== 'default') params.set('sort', sort.value);
      else params.delete('sort');
      const query = params.toString();
      history.replaceState(null, '', query ? `?${query}` : window.location.pathname);
    }

    function readUrl() {
      const params = new URLSearchParams(window.location.search);
      groups.forEach((group) => {
        const values = (params.get(group.dataset.group) || '').split(',').filter(Boolean);
        group.querySelectorAll('[data-value]').forEach((chip) => {
          chip.setAttribute('aria-pressed', String(values.includes(chip.dataset.value)));
        });
      });
      if (inStock) inStock.checked = params.get('stoc') === '1';
      if (sort && params.get('sort')) sort.value = params.get('sort');
    }

    groups.forEach((group) =>
      group.addEventListener('click', (event) => {
        const chip = event.target.closest('[data-value]');
        if (!chip) return;
        chip.setAttribute('aria-pressed', String(chip.getAttribute('aria-pressed') !== 'true'));
        apply();
      })
    );
    inStock?.addEventListener('change', apply);
    sort?.addEventListener('change', apply);
    resets.forEach((button) =>
      button.addEventListener('click', () => {
        root.querySelectorAll('[data-group] [aria-pressed="true"]').forEach((chip) => chip.setAttribute('aria-pressed', 'false'));
        if (inStock) inStock.checked = false;
        if (sort) sort.value = 'default';
        apply();
      })
    );

    // ---- cards ----
    root.querySelectorAll('[data-aq-card]').forEach((card) => {
      const form = card.querySelector('[data-aq-form]');
      if (!form) return;
      const idInput = form.querySelector('[data-aq-variant-id]');
      const qty = form.querySelector('[data-aq-qty]');
      const price = card.querySelector('[data-aq-price]');
      const perKg = card.querySelector('[data-aq-perkg]');
      const total = card.querySelector('[data-aq-total]');
      const msg = card.querySelector('[data-aq-msg]');
      const add = form.querySelector('[data-aq-add]');

      const current = () => {
        const radio = form.querySelector('input[type="radio"]:checked');
        return radio
          ? { price: Number(radio.dataset.price), weight: Number(radio.dataset.weight) }
          : (({ price, weight }) => ({ price: Number(price), weight: Number(weight) || 0 }))(card.closest('[data-aq-item]').dataset);
      };

      function update() {
        const variant = current();
        const quantity = Math.max(1, parseInt(qty?.value, 10) || 1);
        if (price) price.textContent = formatMoney(variant.price, config.moneyFormat);
        const isBag = variant.weight >= 5000;
        if (perKg) {
          perKg.textContent = isBag
            ? fill(config.strings.perKg, { price: formatMoney(Math.round((variant.price * 1000) / variant.weight), config.moneyFormat) })
            : '';
        }
        if (total) {
          total.textContent =
            isBag && quantity > 1
              ? fill(config.strings.total, {
                  kg: formatKg((variant.weight * quantity) / 1000),
                  price: formatMoney(variant.price * quantity, config.moneyFormat),
                })
              : '';
        }
      }

      form.addEventListener('change', (event) => {
        if (event.target.matches('input[type="radio"]')) idInput.value = event.target.value;
        update();
      });
      qty?.addEventListener('input', update);
      form.querySelectorAll('[data-aq-step]').forEach((button) =>
        button.addEventListener('click', () => {
          qty.value = Math.min(999, Math.max(1, (parseInt(qty.value, 10) || 1) + Number(button.dataset.aqStep)));
          update();
        })
      );

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        add.disabled = true;
        msg.textContent = '';
        msg.classList.remove('aq-card__msg--error');
        try {
          // Dawn's cart drawer (when present) is the order step: it shows the
          // real cart, the invoice fields and the checkout button, and stays
          // right when lines are removed. Same request as Dawn's product form.
          const drawer = document.querySelector('cart-drawer');
          const useDrawer = drawer && typeof drawer.renderContents === 'function' && typeof drawer.getSectionsToRender === 'function';
          const body = { items: [{ id: Number(idInput.value), quantity: Math.max(1, parseInt(qty.value, 10) || 1) }] };
          if (useDrawer) {
            body.sections = drawer.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
          }
          const response = await fetch(`${config.cartAddUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          const result = await response.json();
          if (!response.ok) throw new Error(result.description || result.message || config.strings.error);
          msg.textContent = config.strings.added;
          const cart = await (await fetch(`${config.cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
          notifyTheme(cart);
          if (useDrawer && result.sections) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(result);
          }
        } catch (error) {
          msg.textContent = error.message || config.strings.error;
          msg.classList.add('aq-card__msg--error');
        } finally {
          add.disabled = false;
        }
      });

      update();
    });

    readUrl();
    apply();

  }

  const start = () => document.querySelectorAll('[data-aq-catalog]').forEach(initCatalog);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
